import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Info, AlertTriangle, Siren, ExternalLink, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Check, X, ClipboardList, Loader2, GripVertical } from 'lucide-react';
import { apiService } from '../services/api';

// Shared size for right-side banner actions (Okay / Dismiss)
const BANNER_ACTION_BTN =
  'inline-flex items-center justify-center gap-1 min-w-22 h-6 text-xs font-medium px-2.5 rounded border focus:outline-none transition-colors shrink-0 disabled:opacity-50';

const OTHER_OPTION_ID = '__other__';
const OTHER_TEXT_MAX_LENGTH = 500;
const SURVEY_POPUP_WIDTH = 384; // max-w-sm

// ── Simple markdown parser (returns React elements) ───────────────────────

type MarkdownElement = string | React.ReactNode;

function parseMarkdown(text: string): MarkdownElement[] {
  if (!text) return [];
  const elements: MarkdownElement[] = [];
  const lines = text.split('\n');

  for (const line of lines) {
    // Headers
    if (line.startsWith('### ')) {
      elements.push(<h3 key={Math.random()} className="text-sm font-semibold mt-2 mb-1">{line.slice(4)}</h3>);
    } else if (line.startsWith('## ')) {
      elements.push(<h2 key={Math.random()} className="text-sm font-semibold mt-2 mb-1">{line.slice(3)}</h2>);
    } else if (line.startsWith('# ')) {
      elements.push(<h1 key={Math.random()} className="text-sm font-semibold mt-2 mb-1">{line.slice(2)}</h1>);
    } else if (line.trim() === '') {
      elements.push(<br key={Math.random()} />);
    } else {
      // Parse inline markdown (bold, italic, links)
      const parts: MarkdownElement[] = [];
      let remaining = line;
      let key = 0;

      while (remaining.length > 0) {
        // Bold **text**
        const boldMatch = remaining.match(/^\*\*(.*?)\*\*/);
        if (boldMatch) {
          parts.push(<strong key={key++}>{boldMatch[1]}</strong>);
          remaining = remaining.slice(boldMatch[0].length);
          continue;
        }

        // Italic *text*
        const italicMatch = remaining.match(/^\*([^*]+)\*/);
        if (italicMatch) {
          parts.push(<em key={key++}>{italicMatch[1]}</em>);
          remaining = remaining.slice(italicMatch[0].length);
          continue;
        }

        // Link [text](url)
        const linkMatch = remaining.match(/^\[([^\]]+)\]\(([^)]+)\)/);
        if (linkMatch) {
          parts.push(
            <a key={key++} href={linkMatch[2]} target="_blank" rel="noopener noreferrer" className="underline">
              {linkMatch[1]}
            </a>
          );
          remaining = remaining.slice(linkMatch[0].length);
          continue;
        }

        // Plain text (take one char at a time to avoid overlapping patterns)
        parts.push(remaining[0]);
        remaining = remaining.slice(1);
      }

      elements.push(<span key={Math.random()}>{parts}</span>);
    }
  }

  return elements;
}

// ── Types ──────────────────────────────────────────────────────────────────

type NotificationSeverity = 'info' | 'warning' | 'critical';

interface Notification {
  notificationId: string;
  type: string;
  title: string;
  message: string;
  description?: string;
  severity: NotificationSeverity;
  dismissible: boolean;
  active?: boolean;
  dismissed?: boolean;
  broadcast?: boolean;
  ctaLabel?: string;
  ctaUrl?: string;
  ctaType?: string;
  expiresAt?: string;
  createdAt: string;
}

interface SurveyOption {
  optionId: string;
  label: string;
}

interface Survey {
  surveyId: string;
  title: string;
  question: string;
  description?: string;
  options: SurveyOption[];
  allowOther?: boolean;
  dismissible?: boolean;
  answered?: boolean;
  dismissed?: boolean;
  createdAt: string;
}

// ── Severity styling ───────────────────────────────────────────────────────

const SEVERITY_STYLES: Record<NotificationSeverity, {
  wrapper: string;
  expandedWrapper: string;
  icon: typeof Info;
  iconClass: string;
  titleClass: string;
  textClass: string;
  descClass: string;
  ctaClass: string;
  expandClass: string;
  ackClass: string;
}> = {
  info: {
    wrapper: 'bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800',
    expandedWrapper: 'bg-blue-50/80 dark:bg-blue-950/60 border-blue-200 dark:border-blue-800',
    icon: Info,
    iconClass: 'text-blue-500 dark:text-blue-400',
    titleClass: 'text-blue-900 dark:text-blue-100',
    textClass: 'text-blue-800 dark:text-blue-200',
    descClass: 'text-blue-700 dark:text-blue-300',
    ctaClass: 'text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100 underline',
    expandClass: 'text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200',
    ackClass: 'border-blue-400 dark:border-blue-600 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/40',
  },
  warning: {
    wrapper: 'bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800',
    expandedWrapper: 'bg-amber-50/80 dark:bg-amber-950/60 border-amber-200 dark:border-amber-800',
    icon: AlertTriangle,
    iconClass: 'text-amber-500 dark:text-amber-400',
    titleClass: 'text-amber-900 dark:text-amber-100',
    textClass: 'text-amber-800 dark:text-amber-200',
    descClass: 'text-amber-700 dark:text-amber-300',
    ctaClass: 'text-amber-700 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-100 underline',
    expandClass: 'text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200',
    ackClass: 'border-amber-400 dark:border-amber-600 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40',
  },
  critical: {
    wrapper: 'bg-red-50 dark:bg-red-950/40 border-red-200 dark:border-red-800',
    expandedWrapper: 'bg-red-50/80 dark:bg-red-950/60 border-red-200 dark:border-red-800',
    icon: Siren,
    iconClass: 'text-red-500 dark:text-red-400',
    titleClass: 'text-red-900 dark:text-red-100',
    textClass: 'text-red-800 dark:text-red-200',
    descClass: 'text-red-700 dark:text-red-300',
    ctaClass: 'text-red-700 dark:text-red-300 hover:text-red-900 dark:hover:text-red-100 underline',
    expandClass: 'text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200',
    ackClass: 'border-red-400 dark:border-red-600 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40',
  },
};

// ── Draggable survey popup card ────────────────────────────────────────────

interface SurveyPopupCardProps {
  survey: Survey;
  index: number;
  currentSurveyNumber: number;
  totalSurveys: number;
  isSubmitting: boolean;
  isDismissing: boolean;
  otherText: string;
  submitError?: string;
  onRespond: (optionId: string, otherText?: string) => void;
  onDismiss: () => void;
  onOtherTextChange: (value: string) => void;
  onPreviousSurvey: () => void;
  onNextSurvey: () => void;
}

function SurveyPopupCard({
  survey,
  index,
  currentSurveyNumber,
  totalSurveys,
  isSubmitting,
  isDismissing,
  otherText,
  submitError,
  onRespond,
  onDismiss,
  onOtherTextChange,
  onPreviousSurvey,
  onNextSurvey,
}: SurveyPopupCardProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [draggedPosition, setDraggedPosition] = useState<{ x: number; y: number } | null>(null);
  const position = draggedPosition ?? {
    x: typeof window !== 'undefined' ? Math.max(8, window.innerWidth - SURVEY_POPUP_WIDTH - 20 - index * 16) : 8,
    y: 20 + index * 16,
  };

  // Handle drag start — ignore interactive elements
  const handleMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button, input, textarea, select, a')) return;
    setIsDragging(true);
    setDragOffset({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  // Handle drag move — keep at least part of the card on screen
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const maxX = Math.max(0, window.innerWidth - 48);
      const maxY = Math.max(0, window.innerHeight - 48);
      setDraggedPosition({
        x: Math.min(Math.max(e.clientX - dragOffset.x, -SURVEY_POPUP_WIDTH + 48), maxX),
        y: Math.min(Math.max(e.clientY - dragOffset.y, 0), maxY),
      });
    };

    const handleMouseUp = () => setIsDragging(false);

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  return (
    <div
      className="fixed z-100 w-[calc(100vw-2rem)] max-w-sm bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl border border-zinc-200 dark:border-zinc-700 overflow-hidden"
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        cursor: isDragging ? 'grabbing' : 'default',
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Header — drag handle */}
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-zinc-200 dark:border-zinc-800 cursor-move select-none">
        <div className="flex items-center gap-2 min-w-0">
          <GripVertical className="h-4 w-4 shrink-0 text-zinc-400 dark:text-zinc-500" aria-hidden="true" />
          <ClipboardList className="h-4 w-4 shrink-0 text-indigo-600 dark:text-indigo-400" aria-hidden="true" />
          <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate"><span>{survey.title}</span></p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {totalSurveys > 1 && (
            <>
              <button
                type="button"
                onClick={onPreviousSurvey}
                className="p-1 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                aria-label="Previous survey"
              >
                <ChevronLeft className="h-4 w-4 text-zinc-500" aria-hidden="true" />
              </button>
              <span className="text-[11px] text-zinc-500 dark:text-zinc-400" aria-live="polite">
                {currentSurveyNumber}/{totalSurveys}
              </span>
              <button
                type="button"
                onClick={onNextSurvey}
                className="p-1 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                aria-label="Next survey"
              >
                <ChevronRight className="h-4 w-4 text-zinc-500" aria-hidden="true" />
              </button>
            </>
          )}
          {survey.dismissible !== false && (
            <button
              type="button"
              onClick={onDismiss}
              disabled={isDismissing || isSubmitting}
              className="p-1 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors shrink-0 disabled:opacity-50"
              aria-label="Dismiss survey"
            >
              {isDismissing
                ? <Loader2 className="h-4 w-4 animate-spin text-zinc-500" aria-hidden="true" />
                : <X className="h-4 w-4 text-zinc-500" aria-hidden="true" />}
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-3">
        <p className="text-sm text-zinc-700 dark:text-zinc-300"><span>{survey.question}</span></p>
        <div className="mt-3 flex flex-wrap gap-2">
          {survey.options.map((opt) => (
            <button
              key={opt.optionId}
              type="button"
              disabled={isSubmitting || isDismissing}
              onClick={() => onRespond(opt.optionId)}
              className="px-2.5 py-1 text-xs font-medium rounded border border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-200 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 disabled:opacity-50"
            >
              <span>{isSubmitting ? 'Submitting...' : opt.label}</span>
            </button>
          ))}
        </div>
        {survey.allowOther && (
          <div className="mt-3 flex flex-col gap-2">
            <input
              type="text"
              value={otherText}
              onChange={(e) => onOtherTextChange(e.target.value)}
              placeholder="Other response"
              className="w-full px-2.5 py-1 text-xs rounded border border-indigo-300 dark:border-indigo-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100"
              disabled={isSubmitting || isDismissing}
              maxLength={OTHER_TEXT_MAX_LENGTH}
            />
            <div className="flex items-center justify-between gap-2">
              <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
                {otherText.length}/{OTHER_TEXT_MAX_LENGTH}
              </p>
              <button
                type="button"
                disabled={isSubmitting || isDismissing || !otherText.trim() || otherText.trim().length > OTHER_TEXT_MAX_LENGTH}
                onClick={() => onRespond(OTHER_OPTION_ID, otherText.trim())}
                className="px-2.5 py-1 text-xs font-medium rounded border border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-200 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 disabled:opacity-50"
              >
                <span>{isSubmitting ? 'Submitting...' : 'Submit'}</span>
              </button>
            </div>
          </div>
        )}
        {submitError && (
          <p className="mt-2 text-xs text-red-600 dark:text-red-400">
            {submitError}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Component ──────────────────────────────────────────────────────────────

export default function UserPortalBanners() {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [answeredSurveys, setAnsweredSurveys] = useState<Set<string>>(new Set());
  const [activeSurveyIndex, setActiveSurveyIndex] = useState(0);
  const [dismissedSurveys, setDismissedSurveys] = useState<Set<string>>(new Set());
  const [submittingSurveyId, setSubmittingSurveyId] = useState<string | null>(null);
  const [dismissingSurveyId, setDismissingSurveyId] = useState<string | null>(null);
  const [otherTextBySurveyId, setOtherTextBySurveyId] = useState<Record<string, string>>({});
  const [surveySubmitErrorById, setSurveySubmitErrorById] = useState<Record<string, string>>({});

  const { data } = useQuery<{ notifications: Notification[] }>({
    queryKey: ['notifications'],
    queryFn: async () => {
      try {
        return await apiService.makeRequest('/notifications?all=true&limit=1000');
      } catch {
        // Fail silently — notification display is non-critical
        return { notifications: [] };
      }
    },
  });

  const notifications = data?.notifications || [];

  const { data: surveysData } = useQuery<{ surveys: Survey[] }>({
    queryKey: ['surveys'],
    queryFn: async () => {
      try {
        return await apiService.makeRequest('/surveys');
      } catch {
        return { surveys: [] };
      }
    },
  });

  const surveys = surveysData?.surveys || [];

  const handleAcknowledge = async (notificationId: string) => {
    // Optimistically remove from view
    setDismissed(prev => new Set([...prev, notificationId]));
    try {
      await apiService.makeRequest(`/notifications/${notificationId}/dismiss`, { method: 'POST' });
    } catch {
      // Revert on failure
      setDismissed(prev => {
        const next = new Set(prev);
        next.delete(notificationId);
        return next;
      });
    }
  };

  const toggleExpand = (notificationId: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(notificationId)) { next.delete(notificationId); } else { next.add(notificationId); }
      return next;
    });
  };

  const isExpired = (n: Notification) => !!n.expiresAt && new Date() > new Date(n.expiresAt);

  const handleDownload = async (notificationId: string) => {
    try {
      const { downloadUrl } = await apiService.downloadEmailEventExport(notificationId);
      window.open(downloadUrl, '_blank');
    } catch {
      // Fail silently — a stale/expired link is non-critical
    }
  };

  const visibleNotifications = notifications.filter(n =>
    !dismissed.has(n.notificationId) && !n.dismissed
  );

  const visibleSurveys = surveys.filter(
    (s) =>
      !answeredSurveys.has(s.surveyId) &&
      !dismissedSurveys.has(s.surveyId) &&
      !s.answered &&
      !s.dismissed,
  );

  const handleSurveyResponse = async (surveyId: string, optionId: string, otherText?: string) => {
    setSubmittingSurveyId(surveyId);
    setSurveySubmitErrorById((prev) => {
      if (!prev[surveyId]) return prev;
      const next = { ...prev };
      delete next[surveyId];
      return next;
    });
    try {
      await apiService.makeRequest(`/surveys/${surveyId}/respond`, {
        method: 'POST',
        body: JSON.stringify({ optionId, otherText }),
      });
      setAnsweredSurveys((prev) => new Set([...prev, surveyId]));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to submit survey response';
      setSurveySubmitErrorById((prev) => ({ ...prev, [surveyId]: message }));
    } finally {
      setSubmittingSurveyId(null);
    }
  };

  const handleSurveyDismiss = async (surveyId: string) => {
    setDismissingSurveyId(surveyId);
    setSurveySubmitErrorById((prev) => {
      if (!prev[surveyId]) return prev;
      const next = { ...prev };
      delete next[surveyId];
      return next;
    });
    setDismissedSurveys((prev) => new Set([...prev, surveyId]));
    try {
      await apiService.makeRequest(`/surveys/${surveyId}/dismiss`, { method: 'POST' });
    } catch (error) {
      setDismissedSurveys((prev) => {
        const next = new Set(prev);
        next.delete(surveyId);
        return next;
      });
      const message = error instanceof Error ? error.message : 'Failed to dismiss survey';
      setSurveySubmitErrorById((prev) => ({ ...prev, [surveyId]: message }));
    } finally {
      setDismissingSurveyId(null);
    }
  };

  if (visibleNotifications.length === 0 && visibleSurveys.length === 0) return null;

  return (
    <>
      {visibleNotifications.length > 0 && (
        <div className="w-full">
          {visibleNotifications.map(n => {
            const sev = SEVERITY_STYLES[n.severity] || SEVERITY_STYLES.info;
            const Icon = sev.icon;
            const isExpanded = expanded.has(n.notificationId);
            const hasDescription = !!n.description;

            return (
              <div key={n.notificationId} className={`w-full border-b ${sev.wrapper}`}>
                {/* ── Main bar ── */}
                <div className="px-4 py-2.5">
                  <div className="flex items-center gap-3 max-w-7xl mx-auto">
                    <Icon className={`h-4 w-4 shrink-0 ${sev.iconClass}`} aria-hidden="true" />
                    <div className="flex-1 min-w-0">
                      <span className={`text-xs font-semibold ${sev.titleClass}`}>{n.title}</span>
                      {' '}
                      <span className={`text-xs ${sev.textClass}`}>{n.message}</span>
                      {n.ctaLabel && (n.ctaUrl || n.ctaType === 'download') && (
                        <>
                          {' · '}
                          {n.ctaType === 'download' ? (
                            isExpired(n) ? (
                              <span className={`text-xs ${sev.textClass}`}>Expired</span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleDownload(n.notificationId)}
                                className={`inline-flex items-center gap-0.5 text-xs font-medium ${sev.ctaClass}`}
                              >
                                <span>{n.ctaLabel}</span>
                                <ExternalLink className="h-3 w-3" aria-hidden="true" />
                              </button>
                            )
                          ) : (
                            <a
                              href={n.ctaUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`inline-flex items-center gap-0.5 text-xs font-medium ${sev.ctaClass}`}
                            >
                              {n.ctaLabel}
                              <ExternalLink className="h-3 w-3" aria-hidden="true" />
                            </a>
                          )}
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {n.dismissible && (
                        <button
                          type="button"
                          onClick={() => handleAcknowledge(n.notificationId)}
                          className={`${BANNER_ACTION_BTN} ${sev.ackClass}`}
                          aria-label="Acknowledge notification"
                        >
                          <Check className="h-3 w-3" aria-hidden="true" />
                          <span>Okay</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* ── Expand toggle — only shown when description exists ── */}
                {hasDescription && (
                  <div className={`px-4 pb-1 ${isExpanded ? 'pt-0' : ''}`}>
                    <div className="max-w-7xl mx-auto pl-7">
                      <button
                        type="button"
                        onClick={() => toggleExpand(n.notificationId)}
                        className={`flex items-center gap-1 text-xs font-medium focus:outline-none transition-colors ${sev.expandClass}`}
                        aria-expanded={isExpanded}
                      >
                        {isExpanded
                          ? <><ChevronUp className="h-3 w-3 self-center" aria-hidden="true" /> Hide details</>
                          : <><ChevronDown className="h-3 w-3 self-center" aria-hidden="true" /> Show details</>
                        }
                      </button>
                    </div>
                  </div>
                )}

                {/* ── Expanded description ── */}
                {hasDescription && isExpanded && (
                  <div className={`px-4 pb-3 ${sev.expandedWrapper}`}>
                    <div className="max-w-7xl mx-auto pl-7">
                      <div className={`text-xs leading-relaxed ${sev.descClass}`}>
                        {parseMarkdown(n.description || '')}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

        </div>
      )}

      {visibleSurveys.length > 0 && (() => {
        const displayedSurveyIndex = Math.min(activeSurveyIndex, visibleSurveys.length - 1);
        const survey = visibleSurveys[displayedSurveyIndex];
        return (
          <SurveyPopupCard
            key={survey.surveyId}
            survey={survey}
            index={0}
            currentSurveyNumber={displayedSurveyIndex + 1}
            totalSurveys={visibleSurveys.length}
            isSubmitting={submittingSurveyId === survey.surveyId}
            isDismissing={dismissingSurveyId === survey.surveyId}
            otherText={otherTextBySurveyId[survey.surveyId] || ''}
            submitError={surveySubmitErrorById[survey.surveyId]}
            onRespond={(optionId, otherText) => handleSurveyResponse(survey.surveyId, optionId, otherText)}
            onDismiss={() => handleSurveyDismiss(survey.surveyId)}
            onOtherTextChange={(value) => setOtherTextBySurveyId((prev) => ({ ...prev, [survey.surveyId]: value }))}
            onPreviousSurvey={() => setActiveSurveyIndex((current) => (current - 1 + visibleSurveys.length) % visibleSurveys.length)}
            onNextSurvey={() => setActiveSurveyIndex((current) => (current + 1) % visibleSurveys.length)}
          />
        );
      })()}
    </>
  );
}
